/**
 * @module components/MatchMe
 * @description Q1314 Tier 2 "Match me to a cause" guide: three quick
 * questions → up to three matched causes with the reasons they matched →
 * add one or all to the cart. Rendered only when FEATURES.matchMe is on.
 */
import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { useCauseActions } from "../app/useCauseActions";
import { DEFAULT_GIFT_USD } from "../config/page";
import { CATEGORY_LABELS, PARISH_LABELS } from "../config/taxonomy";
import { matchCauses, type CauseMatch, type MatchAnswers, type PaceAnswer, type PlaceAnswer } from "../domain/match";
import { toggle } from "../domain/query";
import type { Cause, CategoryId, ParishId } from "../domain/types";
import { formatUsd } from "../lib/format";
import { Icon } from "./Icons";
import "./MatchMe.css";

/** Pace options (labels are donor-facing). */
const PACE_LABELS: Readonly<Record<PaceAnswer, string>> = {
    now: "Where help is needed right now",
    lasting: "Long-term, lasting change",
    either: "Either works for me",
};

/**
 * Window event the demo tour dispatches to open the guide. An event (not shared
 * state) because the tour is demo-only: production code never references it.
 */
export const OPEN_MATCH_EVENT: string = "afj:open-match";

/** The guide. `step` 0–2 = questions, 3 = results. */
export function MatchMe({ causes }: { causes: Cause[] }): JSX.Element {
    const { dispatchCart, showToast, setCartOpen } = useGiving();
    const actions = useCauseActions();
    const [open, setOpen] = useState<boolean>(false);
    const [step, setStep] = useState<number>(0);
    const [answers, setAnswers] = useState<MatchAnswers>({ interests: [], place: "anywhere", pace: "either" });
    const section = useRef<HTMLElement>(null);
    const question = useRef<HTMLHeadingElement>(null);
    const [focusOnOpen, setFocusOnOpen] = useState<boolean>(false);

    // Tour "Show me": open at question 1.
    useEffect(() => {
        const onOpen = (): void => {
            setStep(0);
            setOpen(true);
            setFocusOnOpen(true);
        };
        window.addEventListener(OPEN_MATCH_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_MATCH_EVENT, onOpen);
    }, []);

    // After opening: bring the guide fully into view and focus its question.
    useEffect(() => {
        if (!open || !focusOnOpen) return;
        section.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        question.current?.focus({ preventScroll: true });
        setFocusOnOpen(false);
    }, [open, focusOnOpen]);

    // Parishes offered = parishes that actually have causes (data-driven, never a dead option).
    const parishes: ParishId[] = useMemo(
        () => [...new Set(causes.map((c: Cause) => c.parish))].filter((p: ParishId) => p !== "islandwide").sort(),
        [causes],
    );
    const matches: CauseMatch[] = useMemo(() => matchCauses(causes, answers), [causes, answers]);

    const restart = (): void => {
        setStep(0);
        setAnswers({ interests: [], place: "anywhere", pace: "either" });
    };
    const addAll = (): void => {
        matches.forEach((m: CauseMatch) => dispatchCart({ type: "add", causeId: m.cause.id, amountUsd: DEFAULT_GIFT_USD }));
        showToast(`Added ${matches.length} matched causes at ${formatUsd(DEFAULT_GIFT_USD)} each.`);
        setCartOpen(true);
    };

    if (!open) {
        return (
            <section id="match" className="match-cta container" aria-labelledby="match-cta-title">
                <span className="match-cta__icon"><Icon name="sparkle" size={28} /></span>
                <div>
                    <h2 id="match-cta-title" className="match-cta__title">Not sure where to start?</h2>
                    <p>Answer three quick questions and we'll suggest causes that fit what you care about.</p>
                </div>
                <button type="button" className="btn btn--primary btn--lg" onClick={() => setOpen(true)}>Match me to a cause <Icon name="arrow-right" /></button>
            </section>
        );
    }

    return (
        <section id="match" ref={section} className="match container" aria-labelledby="match-title" aria-live="polite">
            <div className="match__head">
                <p className="kicker">Match me to a cause · {step < 3 ? `Question ${step + 1} of 3` : "Your matches"}</p>
                <button type="button" className="icon-btn" onClick={() => { setOpen(false); restart(); }} aria-label="Close the guide"><Icon name="close" /></button>
            </div>

            <div className="match__screen" key={step}>
                {step === 0 && (
                    <>
                        <h2 id="match-title" ref={question} tabIndex={-1} className="match__q">What do you care about most?</h2>
                        <p className="match__hint">Pick as many as you like.</p>
                        <div className="match__options">
                            {(Object.keys(CATEGORY_LABELS) as CategoryId[]).map((id: CategoryId) => (
                                <button key={id} type="button" className="match__option" aria-pressed={answers.interests.includes(id)}
                                    onClick={() => setAnswers({ ...answers, interests: toggle(answers.interests, id) })}>{CATEGORY_LABELS[id]}</button>
                            ))}
                        </div>
                    </>
                )}
                {step === 1 && (
                    <>
                        <h2 id="match-title" className="match__q">Is there a part of Jamaica close to your heart?</h2>
                        <div className="match__options">
                            {(["anywhere", ...parishes] as PlaceAnswer[]).map((p: PlaceAnswer) => (
                                <button key={p} type="button" className="match__option" aria-pressed={answers.place === p}
                                    onClick={() => setAnswers({ ...answers, place: p })}>{p === "anywhere" ? "Anywhere in Jamaica" : PARISH_LABELS[p]}</button>
                            ))}
                        </div>
                    </>
                )}
                {step === 2 && (
                    <>
                        <h2 id="match-title" className="match__q">What kind of impact do you want?</h2>
                        <div className="match__options">
                            {(Object.keys(PACE_LABELS) as PaceAnswer[]).map((p: PaceAnswer) => (
                                <button key={p} type="button" className="match__option" aria-pressed={answers.pace === p}
                                    onClick={() => setAnswers({ ...answers, pace: p })}>{PACE_LABELS[p]}</button>
                            ))}
                        </div>
                    </>
                )}
                {step === 3 && (
                    <>
                        <h2 id="match-title" className="match__q">{matches.length > 0 ? "Causes that fit you" : "No close match yet"}</h2>
                        {matches.length === 0 ? (
                            <p className="match__hint">Try choosing another interest, or browse every cause below.</p>
                        ) : (
                            <ul className="match__results">
                                {matches.map((m: CauseMatch) => (
                                    <li key={m.cause.id} className="match__result">
                                        <img src={m.cause.image.src} alt="" />
                                        <div>
                                            <p className="match__partner">{m.cause.partner}</p>
                                            <p className="match__title">{m.cause.title}</p>
                                            <p className="match__why">Matches: {m.reasons.join(" · ")}</p>
                                        </div>
                                        <button type="button" className="btn btn--ghost" onClick={() => actions.addToCart(m.cause)}><Icon name="cart" /> Add</button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>

            <div className="match__nav">
                {step > 0 && <button type="button" className="btn btn--ghost" onClick={() => setStep(step - 1)}><Icon name="chevron-left" /> Back</button>}
                {step < 3 && (
                    <button type="button" className="btn btn--primary" disabled={step === 0 && answers.interests.length === 0} onClick={() => setStep(step + 1)}>
                        {step === 2 ? "Show my matches" : "Next"} <Icon name="arrow-right" />
                    </button>
                )}
                {step === 3 && matches.length > 0 && <button type="button" className="btn btn--gold btn--lg" onClick={addAll}><Icon name="cart" /> Add all {matches.length} to cart</button>}
                {step === 3 && <button type="button" className="btn btn--ghost" onClick={restart}>Start over</button>}
            </div>
        </section>
    );
}
